import { appLogo, avatarDefault } from '@/assets/images';
import { Stack } from '@/components/layout';
import { AppHeader } from '@/components/layout/app-header';
import { Image } from '@/components/ui';
import { Text } from '@/components/ui/text';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';
import { DrawerActions, useNavigation } from '@react-navigation/native';
import { format } from 'date-fns';
import { TouchableOpacity } from 'react-native';

function formatHeaderDate(date: Date): string {
  // Example: Thursday, Feb.26
  return format(date, 'EEEE, MMM dd');
}

type DashboardHeaderProps = {
  avatarSource?: React.ComponentProps<typeof Image>['source'];
  roleName?: string;
};

export function DashboardHeader({
  avatarSource,
  roleName = STRINGS.home.header.coachLabel,
}: DashboardHeaderProps) {
  const label = formatHeaderDate(new Date());
  const { scale, isTablet } = useScaling();
  const styles = useStyles();
  const avatarSize = scale({ phone: 36, tablet: 30 });
  const avatarRadius = scale({ phone: 18, tablet: 15 });
  const navigation = useNavigation();

  return (
    <>
      <AppHeader backgroundColor="white">
        <AppHeader.Left>
          <Image
            source={appLogo}
            width={scale({ phone: 57, tablet: 60 })}
            height={scale({ phone: 28, tablet: 30 })}
            resizeMode="contain"
          />
        </AppHeader.Left>

        <AppHeader.Title>
          <Text
            variant={
              isTablet
                ? 'Font Inter/Subtitle/Bold 14px'
                : 'Font Montserrat/Subtitle/Subtitle 2 Bold 12px'
            }
            transform="uppercase"
            fontWeight={isTablet ? undefined : '800'}
            style={{ color: colors.Blue }}>
            {label}
          </Text>
        </AppHeader.Title>

        <AppHeader.Right>
          <TouchableOpacity
            style={styles.profileButton}
            activeOpacity={0.8}
            onPress={() => navigation.dispatch(DrawerActions.openDrawer())}>
            <Stack align="center" direction={isTablet ? 'row' : 'column'}>
              <Stack
                width={avatarSize}
                height={avatarSize}
                style={[styles.avatarFrame, { borderRadius: avatarRadius }]}>
                <Image
                  source={avatarSource ?? avatarDefault}
                  width={'100%'}
                  height={'100%'}
                  resizeMode="cover"
                  defaultSource={avatarDefault}
                />
              </Stack>
              <Text
                fontSize={scale({ phone: 8, tablet: 14 })}
                style={[styles.userName, isTablet ? styles.userNameTablet : styles.userNamePhone]}>
                {roleName}
              </Text>
            </Stack>
          </TouchableOpacity>
        </AppHeader.Right>
      </AppHeader>
      <Stack
        width="100%"
        height={scale({ phone: 22, tablet: 0 })}
        backgroundColor="white"
        zIndex={9998}
      />
    </>
  );
}

const useStyles = createStyles(({ scale }) => ({
  profileButton: {
    marginRight: scale(16),
  },
  avatarFrame: {
    borderWidth: 1,
    borderColor: colors.Blue,
    overflow: 'hidden',
  },
  userName: {
    color: colors['10'],
    textAlign: 'center',
  },
  userNamePhone: {
    marginTop: scale(4),
    marginLeft: 0,
  },
  userNameTablet: {
    marginTop: 0,
    marginLeft: scale(8),
  },
}));
